import { Button } from "primereact/button";
import { Checkbox } from "primereact/checkbox";
import { Dropdown } from "primereact/dropdown";
import { useMemo } from "react";
import { useGetAllPackageRulesetsQuery } from "store/queries/packageRuleset";
import { useGetCaseRateRulesetsQuery } from "store/queries/pricing";
import styled from "styled-components";
import { RulesetApplicability } from "types/DefaultRules/DefaultRules";
import {
  ILesserOfChargesGroupedRuleset,
  LesserOfChargesGroupRuleType,
  LesserOfChargesScope,
} from "types/LesserOfCharges/LesserOfCharges";

/**
 * CLE-3058 — "Grouped rules" on a Line charges lesser-of rule. A case rate or
 * package moves the whole payment for a group of codes onto one line, so a
 * per-line cap compares that payment with one line's charge (sometimes $1).
 * A grouped rule caps the group as one unit instead:
 * min(sum of the group's expected, multiplier × sum of its charges).
 */

const Section = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
  margin-bottom: 1.5rem;
`;

const Row = styled.div`
  display: grid;
  grid-template-columns: 10rem 1fr auto auto;
  gap: 0.75rem;
  align-items: center;

  @media (max-width: 720px) {
    grid-template-columns: 1fr;
  }
`;

const Help = styled.span`
  font-size: 0.85rem;
  color: var(--gray-600);
`;

const CheckboxLine = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
`;

const RULE_TYPE_OPTIONS: {
  label: string;
  value: LesserOfChargesGroupRuleType;
}[] = [
  { label: "Case Rate", value: "CASE_RATE" },
  { label: "Package", value: "PACKAGE" },
];

/** A grouped-rule row in the form; `rulesetId` is "" until a rule is picked. */
export type IGroupedRuleRow = ILesserOfChargesGroupedRuleset;

export interface IGroupRuleOption {
  label: string;
  value: string;
}

interface IRuleLike {
  id?: string;
  providerId: string | null;
  payerId?: number | string | null;
  groupName?: string | null;
  applicability?: RulesetApplicability;
  disabledAt?: string | null;
}

/** Grouped rules apply only to a Line charges, payer-provider rule. */
export const groupedRulesAllowed = (
  scope: LesserOfChargesScope,
  applicability: RulesetApplicability
): boolean => scope === "LINE_CHARGES" && applicability === "SCOPED";

/**
 * The case rate / package rules a grouped row may pick: same provider, and the
 * same payer or "all payers" (the API refuses anything else).
 */
export const buildGroupRuleOptions = (
  rules: IRuleLike[],
  providerId: string | null,
  payerId: number | null,
  fallbackLabel: string
): IGroupRuleOption[] => {
  if (!providerId) return [];
  return rules
    .filter(
      (r) =>
        !!r.id &&
        r.providerId === providerId &&
        (r.applicability ?? "SCOPED") === "SCOPED" &&
        (r.payerId === null ||
          r.payerId === undefined ||
          Number(r.payerId) === payerId)
    )
    .map((r) => ({
      value: r.id as string,
      label: `${r.groupName || fallbackLabel}${
        r.payerId === null || r.payerId === undefined ? " (all payers)" : ""
      }${r.disabledAt ? " (disabled)" : ""}`,
    }))
    .sort((a, b) => a.label.localeCompare(b.label));
};

/**
 * What the create/update request sends: the picked rows when grouped rules are
 * allowed, otherwise [] — which clears any saved groups (e.g. after switching
 * to Total charges).
 */
export const groupedRulesetsForSubmit = (
  rows: IGroupedRuleRow[],
  scope: LesserOfChargesScope,
  applicability: RulesetApplicability
): ILesserOfChargesGroupedRuleset[] =>
  groupedRulesAllowed(scope, applicability)
    ? rows
        .filter((r) => r.rulesetId)
        .map((r) => ({
          ruleType: r.ruleType,
          rulesetId: r.rulesetId,
          includeRevenueCodeOnlyLines: r.includeRevenueCodeOnlyLines,
        }))
    : [];

/** A row with no rule picked would be silently dropped — block the save instead. */
export const groupedRulesComplete = (
  rows: IGroupedRuleRow[],
  scope: LesserOfChargesScope,
  applicability: RulesetApplicability
): boolean =>
  !groupedRulesAllowed(scope, applicability) ||
  rows.every((r) => !!r.rulesetId);

interface ILesserOfChargesGroupedRulesProps {
  rows: IGroupedRuleRow[];
  providerId: string | null;
  payerId: number | null;
  onChange: (rows: IGroupedRuleRow[]) => void;
}

export function LesserOfChargesGroupedRules({
  rows,
  providerId,
  payerId,
  onChange,
}: ILesserOfChargesGroupedRulesProps) {
  const { data: caseRates } = useGetCaseRateRulesetsQuery(
    providerId ? { providerId, applicability: ["SCOPED"] } : undefined,
    { skip: !providerId }
  );
  const { data: packages } = useGetAllPackageRulesetsQuery(
    { applicability: ["SCOPED"] },
    { skip: !providerId }
  );

  const caseRateOptions = useMemo(
    () =>
      buildGroupRuleOptions(
        (caseRates?.data ?? []) as IRuleLike[],
        providerId,
        payerId,
        "Unnamed case rate"
      ),
    [caseRates, providerId, payerId]
  );
  const packageOptions = useMemo(
    () =>
      buildGroupRuleOptions(
        (packages?.data ?? []) as IRuleLike[],
        providerId,
        payerId,
        "Unnamed package"
      ),
    [packages, providerId, payerId]
  );

  const updateRow = (index: number, patch: Partial<IGroupedRuleRow>) =>
    onChange(rows.map((r, i) => (i === index ? { ...r, ...patch } : r)));

  return (
    <Section data-testid="lesser-of-grouped-rules">
      <label htmlFor="grouped-rules-add" style={{ fontWeight: 600 }}>
        Grouped rules
      </label>
      <Help>
        Cap a case rate or package as one unit instead of line by line: when the
        chosen rule applied to the claim, its lines are capped together at the
        lesser of their combined expected and the multiplier × their combined
        charges. A line joins the first group it matches. Lines in no group are
        capped on their own.
      </Help>
      {!providerId && <Help>Pick a provider to choose its rules.</Help>}
      {rows.map((row, index) => (
        // eslint-disable-next-line react/no-array-index-key
        <Row key={`grouped-rule-${index}`}>
          <Dropdown
            inputId={`grouped-rule-type-${index}`}
            aria-label="Rule type"
            value={row.ruleType}
            options={RULE_TYPE_OPTIONS}
            onChange={(e) =>
              updateRow(index, { ruleType: e.value, rulesetId: "" })
            }
          />
          <Dropdown
            inputId={`grouped-rule-id-${index}`}
            aria-label="Rule"
            value={row.rulesetId || null}
            options={
              row.ruleType === "CASE_RATE" ? caseRateOptions : packageOptions
            }
            onChange={(e) => updateRow(index, { rulesetId: e.value ?? "" })}
            placeholder={
              row.ruleType === "CASE_RATE"
                ? "Select a case rate"
                : "Select a package"
            }
            filter
            disabled={!providerId}
          />
          <CheckboxLine>
            <Checkbox
              inputId={`grouped-rule-revcode-${index}`}
              checked={row.includeRevenueCodeOnlyLines}
              onChange={(e) =>
                updateRow(index, { includeRevenueCodeOnlyLines: !!e.checked })
              }
            />
            <label htmlFor={`grouped-rule-revcode-${index}`}>
              Include revenue-code-only lines
            </label>
          </CheckboxLine>
          <Button
            type="button"
            icon="pi pi-trash"
            className="p-button-text p-button-danger"
            aria-label="Remove grouped rule"
            onClick={() => onChange(rows.filter((_, i) => i !== index))}
          />
        </Row>
      ))}
      <div>
        <Button
          id="grouped-rules-add"
          type="button"
          label="Add grouped rule"
          icon="pi pi-plus"
          className="p-button-outlined p-button-sm"
          disabled={!providerId}
          onClick={() =>
            onChange([
              ...rows,
              {
                ruleType: "CASE_RATE",
                rulesetId: "",
                includeRevenueCodeOnlyLines: false,
              },
            ])
          }
        />
      </div>
      <Help>
        Include revenue-code-only lines also counts lines billed with no HCPCS
        (supplies 0250/0270/0272, recovery 0370/0710…) in the group.
      </Help>
    </Section>
  );
}
